import * as React from "react"
import { FC, useEffect, useRef, useState } from "react"
import { AdminAssetProps, TableAPI, User, UserTrackShareData, UserYoutubeVideoShareData } from "../../../../types"
import { connect } from "react-redux"
import {
  deleteTrackShareData,
  deleteYoutubeVideoShareData,
  fetchUserTrackShareData,
  fetchUserYoutubeVideoShareData,
  selectCollaboratorData,
  selectTrackShareData,
  selectUser,
  selectYoutubeVideoShareData
} from "../../../../actions/adminActions"
import { RootState } from "../../../../store"
import AdminTrackForm from "./modals/AdminTrackForm.tsx"
import { ADMIN_PERMISSIONS, adminTableLimit, ASSET_TYPES, errorFilledToastOptions } from "../../../../constants"
import adminYoutubeVideoColumns from "./columns/adminYoutubeVideoColumns"
import adminTrackColumns from "./columns/adminTrackColumns"
import AdminYoutubeVideoForm from "./modals/AdminYoutubeVideoForm"
import AdminTrackShareHolderTable from "./AdminTrackShareHolderTable"
import AdminCollaboratorForm from "./modals/AdminCollaboratorForm"
import TableNew from "../../../ui/table/TableNew"
import UserDetailCard from "./UserDetailCard.tsx"
import { KButton, KDropdown, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import ConfirmationDialog from "../../../ui/modals/ConfirmationDialog.tsx"
import { handleAuthorizedRequest, trackShareHolders, users } from "../../../../apis/index.ts"
import { checkAdminPermission, mapPaginatedDataToCamelCase, mapToCamelCase } from "../../../../utility/index.ts"
import { toast } from "react-toastify"
import Loading from "../../../ui/Loading"

const AdminAsset: FC<AdminAssetProps> = (props) => {
  const {
    selectedUser,
    fetchUserTrackShareData,
    fetchUserYoutubeVideoShareData,
    paginatedUserTrackShareData,
    selectTrackShareData,
    deleteTrackShareData,
    selectedTrackShareData,
    paginatedUserYoutubeVideoShareData,
    selectYoutubeVideoShareData,
    deleteYoutubeVideoShareData,
    selectedYoutubeVideoShareData,
    selectCollaboratorData,
    selectedCollaboratorData
  } = props

  const [assetType, setAssetType] = useState(ASSET_TYPES.TRACK)
  const [tableAPI, setTableAPI] = useState<TableAPI | undefined>(undefined)
  const [trackLoading, setTrackLoading] = useState(false)
  const [videoLoading, setVideoLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [timeoutId, setTimeoutId] = useState(undefined)
  const [inputTimeoutId, setInputTimeoutId] = useState(undefined)
  const deleting = useRef(false)
  const creating = useRef(false)
  const divRef = useRef<HTMLDivElement | null>(null)
  const [openAssetTypes, setOpenAssetTypes] = useState<boolean>(false)

  const [openTrackForm, setOpenTrackForm] = useState<boolean>(false)
  const [openYoutubeVideoForm, setOpenYoutubeVideoForm] = useState<boolean>(false)
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false)

  const trackColumns = adminTrackColumns(selectTrackShareData, setOpenTrackForm, setOpenConfirmation, selectedUser?.id)
  const youtubeVideoColumns = adminYoutubeVideoColumns(
    selectYoutubeVideoShareData,
    setOpenYoutubeVideoForm,
    setOpenConfirmation
  )

  const [searchValue, setSearchValue] = useState<string>("")
  const searchOptions: KSelectOption[] = [
    { label: "Title", value: 0, value2: "title" },
    { label: "ISRC", value: 1, value2: "isrc" }
  ]
  const [selectedSearchOption, setSelectedSearchOption] = useState<KSelectOption>(searchOptions[0])
  const [trackPageNumber, setTrackPageNumber] = useState<number>(1)
  const [youtubeVideoPageNumber, setYoutubeVideoPageNumber] = useState<number>(1)
  const [additionalTrackFilters, setAdditionalTrackFilters] = useState<{ [key: string]: string | undefined }>({
    title: "",
    track__isrc: ""
  })
  const [additionalYoutubeVideoFilters, setAdditionalYoutubeVideoFilters] = useState<{
    [key: string]: string | undefined
  }>({ title: "", youtube_video_track_isrc: "" })

  useEffect(() => {
    selectTrackShareData(null)
    selectYoutubeVideoShareData(null)
    selectCollaboratorData(null)
  }, [])

  useEffect(() => {
    selectTrackShareData(null)
  }, [paginatedUserTrackShareData.results])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(event.target as Node)) {
        setOpenAssetTypes(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!selectedUser && props.params.has("user_id")) {
      const user_id = props.params.get("user_id")

      fetchUserDetails(String(user_id))
    }

    if (selectedUser?.id) {
      fetchUserTrackShareData(selectedUser.id, adminTableLimit, 0, {}).then(() => setTrackLoading(false))
      fetchUserYoutubeVideoShareData(selectedUser.id, adminTableLimit, 0, {}).then(() => setVideoLoading(false))
    }
  }, [selectedUser])

  useEffect(() => {
    if (!tableAPI || paginatedUserTrackShareData?.count === undefined) return
    if (assetType === ASSET_TYPES.TRACK) {
      if (creating.current && paginatedUserTrackShareData.count / adminTableLimit > tableAPI.currentPage) {
        const targetPage = Math.ceil(paginatedUserTrackShareData.count / adminTableLimit)
        tableAPI.setCurrentPage(targetPage)
        onClickPage(targetPage, {}).then()
      }
      if (
        deleting.current &&
        paginatedUserTrackShareData.results.length < adminTableLimit &&
        paginatedUserTrackShareData.count >= tableAPI.currentPage * adminTableLimit &&
        selectedUser?.id
      ) {
        fetchUserTrackShareData(
          selectedUser.id,
          adminTableLimit,
          (tableAPI.currentPage - 1) * adminTableLimit,
          {}
        ).then(() => setTrackLoading(false))
      } else if (
        deleting.current &&
        paginatedUserTrackShareData.results.length === 0 &&
        paginatedUserTrackShareData.count > 0
      ) {
        const targetPage = tableAPI.currentPage - 1
        tableAPI.setCurrentPage(targetPage)
        onClickPage(targetPage, {}).then()
      }
    }
    deleting.current = false
    creating.current = false
  }, [paginatedUserTrackShareData.count])

  useEffect(() => {
    if (!tableAPI || paginatedUserYoutubeVideoShareData?.count === undefined) return
    if (assetType === ASSET_TYPES.YOUTUBE_VIDEO) {
      if (creating.current && paginatedUserYoutubeVideoShareData.count / adminTableLimit > tableAPI.currentPage) {
        const targetPage = Math.ceil(paginatedUserYoutubeVideoShareData.count / adminTableLimit)
        tableAPI.setCurrentPage(targetPage)
        onClickPage(targetPage, {}).then()
      }
      if (
        deleting.current &&
        paginatedUserYoutubeVideoShareData.results.length < adminTableLimit &&
        paginatedUserYoutubeVideoShareData.count >= tableAPI.currentPage * adminTableLimit &&
        selectedUser?.id
      ) {
        fetchUserYoutubeVideoShareData(
          selectedUser.id,
          adminTableLimit,
          (tableAPI.currentPage - 1) * adminTableLimit,
          {}
        ).then(() => setVideoLoading(false))
      } else if (
        deleting.current &&
        paginatedUserYoutubeVideoShareData.results.length === 0 &&
        paginatedUserYoutubeVideoShareData.count > 0
      ) {
        const targetPage = tableAPI.currentPage - 1
        tableAPI.setCurrentPage(targetPage)
        onClickPage(targetPage, {}).then()
      }
    }
    deleting.current = false
    creating.current = false
  }, [paginatedUserYoutubeVideoShareData.count])

  const onClickPage = async (
    pageNumber: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) => {
    if (!selectedUser?.id) return
    if (assetType === ASSET_TYPES.TRACK) {
      setTrackPageNumber(pageNumber)
      setTrackLoading(true)
      fetchUserTrackShareData(
        selectedUser.id,
        adminTableLimit,
        (pageNumber - 1) * adminTableLimit,
        filters,
        sortField,
        sortDirection
      ).then(() => setTrackLoading(false))
    } else {
      setYoutubeVideoPageNumber(pageNumber)
      setVideoLoading(true)
      fetchUserYoutubeVideoShareData(
        selectedUser.id,
        adminTableLimit,
        (pageNumber - 1) * adminTableLimit,
        filters,
        sortField,
        sortDirection
      ).then(() => setVideoLoading(false))
    }
  }

  const onClickAdd = () => {
    if (assetType === ASSET_TYPES.TRACK) {
      setOpenTrackForm(true)
      selectTrackShareData(new UserTrackShareData())
    } else {
      setOpenYoutubeVideoForm(true)
      selectYoutubeVideoShareData(new UserYoutubeVideoShareData())
    }
  }

  const onClickDelete = () => {
    if (assetType === ASSET_TYPES.TRACK) {
      deleteTrackShareData(selectedTrackShareData.id)
    } else {
      deleteYoutubeVideoShareData(selectedYoutubeVideoShareData.id)
    }
    setOpenConfirmation(false)
  }

  const onSelectRow = (index: number) => {
    deleting.current = true
    if (assetType === ASSET_TYPES.TRACK) {
      selectTrackShareData(paginatedUserTrackShareData.results[index])
    } else {
      selectYoutubeVideoShareData(paginatedUserYoutubeVideoShareData.results[index])
    }
  }

  const onSortOrFilterChange = async (
    filters: { [key: string]: string | number | boolean },
    sortField: string,
    sortDirection?: "asc" | "desc",
    debounce?: boolean
  ) => {
    if (timeoutId) clearTimeout(timeoutId)
    setTimeoutId(
      //@ts-ignore
      setTimeout(
        async () => {
          if (!selectedUser?.id) return
          if (assetType === ASSET_TYPES.TRACK) {
            setTrackLoading(true)
            await fetchUserTrackShareData(selectedUser.id, adminTableLimit, 0, filters, sortField, sortDirection)
            setTrackLoading(false)
            setTrackPageNumber(1)
          } else {
            setVideoLoading(true)
            await fetchUserYoutubeVideoShareData(selectedUser.id, adminTableLimit, 0, filters, sortField, sortDirection)
            setVideoLoading(false)
            setYoutubeVideoPageNumber(1)
          }
        },
        debounce ? 500 : 0
      )
    )
  }

  const fetchUserDetails = async (userId: string) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await users(`/?id=${userId}`, "get")
        if (response.status === 200) {
          const user: User = mapToCamelCase(response.data["results"][0])
          props.selectUser(user)
        }
      },
      "Error fetching users details ",
      undefined
    )
  }

  useEffect(() => {
    clearTimeout(inputTimeoutId)
    setInputTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (selectedSearchOption?.value2) {
          if (assetType === ASSET_TYPES.TRACK) {
            setAdditionalTrackFilters({
              title: selectedSearchOption?.value2 === "title" ? searchValue : "",
              track__isrc: selectedSearchOption?.value2 === "isrc" ? searchValue : ""
            })
          } else if (assetType === ASSET_TYPES.YOUTUBE_VIDEO) {
            setAdditionalYoutubeVideoFilters({
              title: selectedSearchOption?.value2 === "title" ? searchValue : "",
              youtube_video_track_isrc: selectedSearchOption?.value2 === "isrc" ? searchValue : ""
            })
          }
        }
      }, 500)
    )
  }, [selectedSearchOption, searchValue, assetType])

  const onClickExportTracks = async () => {
    if (!selectedUser?.id) return
    const totalCount = paginatedUserTrackShareData.count || 0
    if (totalCount === 0) {
      toast.error("No tracks to export", errorFilledToastOptions)
      return
    }

    setExportLoading(true)
    try {
      const response = await trackShareHolders(
        `/?user=${selectedUser.id}&limit=${totalCount}&offset=0`,
        "get"
      )
      const data = mapPaginatedDataToCamelCase<UserTrackShareData>(response.data)
      const headers = [
        "ALBUM_NAME",
        "TRACK_NAME",
        "UPC",
        "ISRC",
        "LYRICIST",
        "COMPOSER",
        "PRODUCER",
        "LABEL"
      ]
      const rows = data.results.map((item: UserTrackShareData) => [
        item.trackDetail?.releaseDetail?.title || "",
        item.trackDetail?.title || "",
        item.trackDetail?.releaseDetail?.upc || "",
        item.trackDetail?.isrc || "",
        item.trackDetail?.lyricist || "",
        item.trackDetail?.composer || "",
        item.trackDetail?.producer || "",
        item.trackDetail?.releaseDetail?.labelDetail?.name || ""
      ])
      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell: string) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n")
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.setAttribute("download", `user_${selectedUser.id}_tracks_export.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      toast.error("Error exporting tracks", errorFilledToastOptions)
    } finally {
      setExportLoading(false)
    }
  }

  const topRightElements = () => (
    <div className="flex flex-row gap-2.5 items-center">
      <div
        className="flex items-center py-[13px] pl-3.5 pr-3 gap-2 rounded-[10px] w-[300px]"
        style={{
          background: searchValue ? "#FFF" : "#F7F7F7",
          border: "1px solid #F3F3F3",
          height: "48px",
          transition: "0.3s"
        }}
      >
        <KInput
          dataTestId="search-asset-input"
          value={searchValue}
          padding="0px"
          background="transparent"
          onChange={(text: string) => {
            setSearchValue(text)
          }}
          leftIcon="/catalog_icons/search-grey.svg"
          gap="8px"
          borderRadius={0}
          shadowDisabled
          placeholder={"Search..."}
        />
        <KDropdown
          dataTestId="search-asset-dropdown"
          rightIcon="/admin_icons/caret-down.svg"
          options={searchOptions}
          selected={selectedSearchOption}
          onSelect={(selected) => {
            setSelectedSearchOption(selected as KSelectOption)
          }}
          padding="0px"
          background="transparent"
          activeBackground="transparent"
          shadowDisabled={true}
          enableIndicator={true}
          menuWidth={140}
          width={140}
          menuLeftMargin={-40}
          textColor="#F3F3F3"
        />
      </div>
      {assetType === ASSET_TYPES.TRACK && (
        <KButton
          dataTestId="export-tracks-button"
          text="Export"
          leftIcon="/account_icons/download-line.svg"
          gap="8px"
          onClick={onClickExportTracks}
          background="#F7F7F7"
          hoverBackground="#F3F3F3"
          textColor="#000"
          padding="0 16px"
          disabled={!selectedUser?.id || paginatedUserTrackShareData.count === 0 || exportLoading}
          width="120px"
          height="48px"
          fontSize={14}
        />
      )}
      <KButton
        dataTestId="add-track-button"
        icon="/admin_icons/plus.svg"
        onClick={onClickAdd}
        background="#F7F7F7"
        hoverBackground="#F3F3F3"
        padding="14px"
        disabled={!selectedUser?.id}
        width="48px"
        height="48px"
      />
    </div>
  )

  const renderTitleSwitch = () => {
    return (
      <div className="flex items-center">
        {assetType === ASSET_TYPES.TRACK ? (
          <div className={`flex items-center pt-2 px-3 cursor-pointer`} onClick={() => setAssetType(ASSET_TYPES.TRACK)}>
            <KTitleSpan
              text={`Tracks (${paginatedUserTrackShareData.count})`}
              fontSize={32}
              lineHeight="40px"
              color="#000"
            />
          </div>
        ) : (
          <div
            className={`flex items-center pt-2 px-3 cursor-pointer`}
            onClick={() => setAssetType(ASSET_TYPES.YOUTUBE_VIDEO)}
          >
            <KTitleSpan
              text={`Youtube Videos (${paginatedUserYoutubeVideoShareData.count})`}
              fontSize={32}
              lineHeight="40px"
              color="#000"
            />
          </div>
        )}
        <div className="relative" ref={divRef}>
          <KButton
            dataTestId="open-asset-type"
            padding="6px"
            height="32px"
            width="32px"
            background="#F7F7F7"
            hoverBackground="#F3F3F3"
            icon="/admin_icons/caret-down.svg"
            onClick={() => {
              setOpenAssetTypes(!openAssetTypes)
            }}
          />
          {openAssetTypes && (
            <div className="flex shadow-lg flex-col gap-1 w-[200px] rounded-[10px] p-1 bg-[#F7F7F7] absolute top-10">
              <div
                className="py-2.5 px-2 flex flex-row justify-between hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                onClick={() => {
                  setAssetType(ASSET_TYPES.TRACK)
                  setOpenAssetTypes(false)
                  selectYoutubeVideoShareData(null)
                }}
              >
                <KSpan text="Tracks" color="#000" />
                <KSpan text={`${paginatedUserTrackShareData.count}`} color="#000" />
              </div>
              <div
                className="py-2.5 px-2 flex flex-row justify-between hover:bg-[#F3F3F3] rounded-[10px] cursor-pointer"
                onClick={() => {
                  setAssetType(ASSET_TYPES.YOUTUBE_VIDEO)
                  setOpenAssetTypes(false)
                  selectTrackShareData(null)
                }}
              >
                <KSpan text="Youtube Videos" color="#000" />
                <KSpan text={`${paginatedUserYoutubeVideoShareData.count}`} color="#000" />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return checkAdminPermission(ADMIN_PERMISSIONS.USER_ASSET_WRITE, props.loggedInUser?.adminPermissions) ? (
    <div className="flex flex-row gap-3 overflow-hidden">
      {exportLoading && <Loading />}
      <div className="w-auto">
        {selectedUser && (
          <UserDetailCard setModalOption={props.setModalOption} setTab={props.setTab} setParams={props.setParams} />
        )}
      </div>
      <div className="flex flex-col flex-grow max-h-[100vh] overflow-auto">
        <div className="mt-2 mb-2 container flex gap-4 pl-1">
          <TableNew
            hidden={assetType !== ASSET_TYPES.TRACK}
            title={renderTitleSwitch()}
            size={"sm"}
            className="flex-grow"
            pagination
            limit={adminTableLimit}
            columns={trackColumns}
            minRowCount={adminTableLimit}
            data={paginatedUserTrackShareData.results}
            count={paginatedUserTrackShareData.count}
            onClickPage={onClickPage}
            topRightElement={topRightElements()}
            onSelectRow={onSelectRow}
            setTableAPI={setTableAPI}
            loading={trackLoading}
            onSortOrFilterChange={onSortOrFilterChange}
            overrideTableOverflow="auto"
            pageNumber={trackPageNumber}
            additionalFilters={additionalTrackFilters}
          />
          <TableNew
            hidden={assetType !== ASSET_TYPES.YOUTUBE_VIDEO}
            title={renderTitleSwitch()}
            size={"sm"}
            className="flex-grow"
            pagination
            limit={adminTableLimit}
            columns={youtubeVideoColumns}
            minRowCount={adminTableLimit}
            data={paginatedUserYoutubeVideoShareData.results}
            count={paginatedUserYoutubeVideoShareData.count}
            onClickPage={onClickPage}
            topRightElement={topRightElements()}
            onSelectRow={onSelectRow}
            setTableAPI={setTableAPI}
            loading={videoLoading}
            onSortOrFilterChange={onSortOrFilterChange}
            overrideTableOverflow="auto"
            pageNumber={youtubeVideoPageNumber}
            additionalFilters={additionalYoutubeVideoFilters}
          />
          {selectedUser?.id && (
            <div>
              <AdminTrackForm
                open={assetType === ASSET_TYPES.TRACK && openTrackForm}
                onClose={() => {
                  setOpenTrackForm(false)
                }}
                creatingRef={creating}
              />
              <AdminYoutubeVideoForm
                open={assetType === ASSET_TYPES.YOUTUBE_VIDEO && openYoutubeVideoForm}
                onClose={() => {
                  setOpenYoutubeVideoForm(false)
                }}
                creatingRef={creating}
              />
            </div>
          )}
        </div>
        {selectedTrackShareData?.trackDetail.id && (
          <div className="w-full mt-2 mb-2 container flex gap-4 pl-1">
            <div className="w-full">
              <AdminTrackShareHolderTable trackId={selectedTrackShareData?.trackDetail.id} />
            </div>

            <div>
              <AdminCollaboratorForm
                open={selectedCollaboratorData !== null}
                onClose={() => {
                  selectCollaboratorData(null)
                }}
              />
            </div>
          </div>
        )}
      </div>
      <ConfirmationDialog
        openConfirmation={openConfirmation}
        setOpenConfirmation={setOpenConfirmation}
        loading={false}
        handleConfirm={onClickDelete}
        overrideDescription={
          assetType === ASSET_TYPES.TRACK
            ? `This action will delete track share: ${selectedTrackShareData?.trackDetail.title}`
            : `This action will delete youtube video share: ${selectedYoutubeVideoShareData?.youtubeVideoDetail.title}`
        }
      />
    </div>
  ) : (
    <div />
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    loggedInUser: state.auth.user,
    selectedUser: state.admin.selectedUser,
    selectedTrackShareData: state.admin.selectedTrackShareData,
    selectedYoutubeVideoShareData: state.admin.selectedYoutubeVideoShareData,
    paginatedUserTrackShareData: state.admin.paginatedUserTrackShareData,
    paginatedUserYoutubeVideoShareData: state.admin.paginatedUserYoutubeVideoShareData,
    selectedCollaboratorData: state.admin.selectedCollaboratorData
  }
}

export default connect(mapStateToProps, {
  selectUser,
  fetchUserTrackShareData,
  fetchUserYoutubeVideoShareData,
  selectTrackShareData,
  deleteTrackShareData,
  selectYoutubeVideoShareData,
  deleteYoutubeVideoShareData,
  selectCollaboratorData
})(AdminAsset)
